import { FC, useState, MouseEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmationDialogV2 from 'src/components/DialogBox/ConfirmationDialogV2/ConfirmationDialogV2';
import RejectDialogV2 from 'src/components/DialogBox/RejectDialogV2/RejectDialogV2';
import {
  apiUrls,
  routeURL,
  voucherDialogTitles,
  voucherDialogSubtitles,
  userRoles,
  dataStorageKeywords,
  voucherActionButtons,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { VoucherStatus } from 'src/models/enums/VoucherStatus.enum';
import { VoucherViewData } from 'src/models/Management.interface';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import VoucherFormDialog from 'src/components/DialogBox/VoucherFormDialog/VoucherFormDialog';
import { generateVoucherPDF } from 'src/components/Invoice/VoucherPDF';

const VoucherActionMenuButton: FC<{
  rowId: string;
  status: VoucherStatus;
  fetchVouchers: () => void;
  deleteVoucher: (id: string) => void;
}> = ({ rowId, status, fetchVouchers, deleteVoucher }) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { showSnackbar } = StyledSnackbar();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] =
    useState<VoucherViewData | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  const isAdminOrOwner =
    userRole === userRoles.ADMIN || userRole === userRoles.OWNER;

  const showView = true;
  const showEdit = status === VoucherStatus.PENDING_APPROVAL;
  const showApprove =
    status === VoucherStatus.PENDING_APPROVAL && isAdminOrOwner;
  const showReject =
    status === VoucherStatus.PENDING_APPROVAL && isAdminOrOwner;
  const showDelete = status === VoucherStatus.PENDING_APPROVAL;
  const showVoucherPayments =
    status === VoucherStatus.APPROVED ||
    status === VoucherStatus.PAYMENT_PROGRESS ||
    status === VoucherStatus.PAYMENT_COMPLETED;

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const fetchVoucherById = async (): Promise<VoucherViewData | null> => {
    try {
      const response = await axiosTokenInstance.get(
        `${apiUrls.GET_VOUCHER_BY_ID}`,
        {
          params: {
            id: rowId,
          },
        }
      );
      return response.data;
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      return null;
    }
  };

  const handleView = () => {
    handleClose();
    setSearchParams({ view: rowId });
  };

  const handleEdit = async () => {
    handleClose();
    const voucher = await fetchVoucherById();
    if (voucher) {
      setSelectedVoucher(voucher);
      setEditDialogOpen(true);
    }
  };

  const handleApprove = () => {
    handleClose();
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (approveLoading) return;
    setApproveLoading(true);
    try {
      const response = await axiosTokenInstance.patch(apiUrls.VOUCHER_STATUS, {
        id: rowId,
        status: 'Approved',
      });
      showSnackbar(
        response.data.message || 'Voucher approved successfully',
        'success'
      );
      fetchVouchers();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setApproveLoading(false);
      setApproveDialogOpen(false);
    }
  };

  const handleReject = () => {
    handleClose();
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    if (rejectLoading) return;
    setRejectLoading(true);
    try {
      const response = await axiosTokenInstance.patch(apiUrls.VOUCHER_STATUS, {
        id: rowId,
        status: 'Rejected',
        rejectionNotes: reason,
      });
      showSnackbar(
        response.data.message || 'Voucher rejected successfully',
        'success'
      );
      fetchVouchers();
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setRejectLoading(false);
      setRejectDialogOpen(false);
    }
  };

  const handleDeleteClick = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteVoucher(rowId);
    setDeleteDialogOpen(false);
  };

  const handleVoucherPayments = () => {
    handleClose();
    navigate(routeURL.VOUCHER_PAYMENTS_REDIRECT(rowId));
  };

  const handlePrint = async () => {
    handleClose();
    const voucher = await fetchVoucherById();
    if (voucher) {
      await generateVoucherPDF(voucher);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedVoucher(null);
  };

  const handleEditSuccess = () => {
    handleCloseEditDialog();
    fetchVouchers();
  };

  return (
    <>
      <ActionMenuButton
        id="action-menu-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <i className="bx bx-dots-horizontal-rounded"></i>
      </ActionMenuButton>

      <ActionMenuPopover
        id="action-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <MenuItemsWrapper>
          {showView && (
            <MenuItemWithIcon
              onClick={handleView}
              iconClassName="bx bx-file-find"
              text={voucherActionButtons.VIEW}
            />
          )}

          {showEdit && (
            <MenuItemWithIcon
              onClick={handleEdit}
              iconClassName="bx bx-edit"
              text={voucherActionButtons.EDIT}
            />
          )}

          {showApprove && (
            <MenuItemWithIcon
              onClick={handleApprove}
              iconClassName="bx bx-check"
              text={voucherActionButtons.APPROVE}
            />
          )}

          {showReject && (
            <MenuItemWithIcon
              onClick={handleReject}
              iconClassName="bx bx-x"
              text={voucherActionButtons.REJECT}
            />
          )}

          {showDelete && (
            <MenuItemWithIcon
              onClick={handleDeleteClick}
              iconClassName="bx bx-trash"
              text={voucherActionButtons.DELETE}
            />
          )}

          {showVoucherPayments && (
            <MenuItemWithIcon
              onClick={handleVoucherPayments}
              iconClassName="bx bx-money"
              text={voucherActionButtons.VOUCHER_PAYMENTS}
            />
          )}

          <MenuItemWithIcon
            onClick={handlePrint}
            iconClassName="bx bx-printer"
            text={voucherActionButtons.PRINT}
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <VoucherFormDialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        onSuccess={handleEditSuccess}
        data={selectedVoucher}
        mode="edit"
      />

      <ConfirmationDialogV2
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveConfirm}
        title={voucherDialogTitles.APPROVE}
        subtitle={voucherDialogSubtitles.APPROVE}
        subtitleSecondary={voucherDialogSubtitles.APPROVE_SECONDARY}
        confirmText={voucherActionButtons.APPROVE}
        confirmButtonColor="var(--success)"
        iconColor="var(--warning)"
      />

      <RejectDialogV2
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
        title={voucherDialogTitles.REJECT}
        subtitle={voucherDialogSubtitles.REJECT}
        subtitleSecondary={voucherDialogSubtitles.REJECT_SECONDARY}
        placeholder="Reason for rejection"
        confirmText={voucherActionButtons.REJECT}
        loading={rejectLoading}
      />

      <ConfirmationDialogV2
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={voucherDialogTitles.DELETE}
        subtitle={voucherDialogSubtitles.DELETE}
        subtitleSecondary={voucherDialogSubtitles.DELETE_SECONDARY}
        confirmText={voucherActionButtons.DELETE}
        confirmButtonColor="var(--error)"
        iconColor="var(--error)"
      />
    </>
  );
};

export default VoucherActionMenuButton;
